/**
 * AppsPage - Main applications management page
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppData, useApiService } from '../../services/apiService';
import AppsList from '../organisms/AppsList';
import { Button } from '../atoms/Button';

interface AppsPageProps {
  className?: string;
}

const AppsPage: React.FC<AppsPageProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const apiService = useApiService();
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [appsListKey, setAppsListKey] = useState(0); // For forcing refresh

  const handleAppClick = (app: AppData) => {
    navigate(`/apps/${app.app_id}`);
  };

  const handleSyncApps = async () => {
    setSyncing(true);
    setSyncResult(null);
    
    try {
      const response = await apiService.syncAllAppsFromTenant(false, 1000);
      const result = response.data;
      
      setSyncResult(
        `Synced ${result.synced_apps} apps: ${result.created_apps} created, ${result.updated_apps} updated, ${result.failed_apps} failed`
      );
      
      // Force refresh the apps list
      setAppsListKey(prev => prev + 1);
      
    } catch (error) {
      setSyncResult(`Sync failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Application Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage applications and their feedback settings
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <Button
            onClick={handleSyncApps}
            disabled={syncing}
            variant="primary"
            size="sm"
          >
            {syncing ? 'Syncing...' : 'Sync Apps'}
          </Button>
        </div>
      </div>

      {syncResult && (
        <div className={`p-4 rounded-lg ${syncResult.includes('failed') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
          {syncResult}
        </div>
      )}

      <AppsList
        key={appsListKey}
        onAppClick={handleAppClick}
        showFilters={true}
        showPagination={true}
        defaultPageSize={12}
      />
    </div>
  );
};

export default AppsPage;